import { useEffect, useState } from 'react';

import { match } from '@formatjs/intl-localematcher';

/**
 * TODO: Fix this properly with going into the translations and removing the HTML entities. Also properly escape ' character in ITALIAN.
 * Also make sure that the translations are not broken when we add new languages.
 */
export const normalizeMessageText = (text: string): string => {
	let normalized = text;

	normalized = normalized.replace(/&quot;/g, '"');
	normalized = normalized.replace(/&apos;/g, "'");
	normalized = normalized.replace(/&#x27;/g, "'");
	normalized = normalized.replace(/&#x2F;/g, '/');
	normalized = normalized.replace(/&lt;/g, '<');
	normalized = normalized.replace(/&gt;/g, '>');
	normalized = normalized.replace(/&amp;/g, '&');

	// ICU MessageFormat treats `'` as an escape character: one directly before a syntax
	// character such as `<` or `{` turns the rest of the message into literal text (in Italian,
	// "dell'<b>…" escaped the bold tag). Swap it for a character ICU does not treat as syntax.
	// The typographic apostrophe (U+2019) reads correctly; the backtick it replaces rendered
	// "don`t" in every locale, including English.
	normalized = normalized.replace(/'/g, '\u2019');

	return normalized;
};

export const AVAILABLE_LOCALES = [
	'cs',
	'da',
	'de',
	'en',
	'en-GB',
	'en-ZZ',
	'es',
	'fi',
	'fr',
	'hr',
	'hu',
	'it',
	'ja',
	'ko',
	'nb',
	'nl',
	'pl',
	'pt-BR',
	'ru',
	'sl',
	'sr-RS',
	'sr-YR',
	'sv',
	'th',
	'tr',
	'uk',
	'vi',
	'zh',
	'zh-TW',
] as const;

export type AvailableLocale = (typeof AVAILABLE_LOCALES)[number];

const AVAILABLE_LOCALES_SET = new Set<string>(AVAILABLE_LOCALES);

const isAvailableLocale = (locale: string): locale is AvailableLocale => {
	return AVAILABLE_LOCALES_SET.has(locale);
};

const importLocaleMessages = async (locale: AvailableLocale) => {
	switch (locale) {
		case 'cs':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/cs" */ '../../../i18n/cs'
			);
		case 'da':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/da" */ '../../../i18n/da'
			);
		case 'de':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/de" */ '../../../i18n/de'
			);
		case 'en':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/en" */ '../../../i18n/en'
			);
		case 'en-GB':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/en_GB" */ '../../../i18n/en_GB'
			);
		case 'en-ZZ':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/en_ZZ" */ '../../../i18n/en_ZZ'
			);
		case 'es':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/es" */ '../../../i18n/es'
			);
		case 'fi':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/fi" */ '../../../i18n/fi'
			);
		case 'fr':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/fr" */ '../../../i18n/fr'
			);
		case 'hr':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/hr" */ '../../../i18n/hr'
			);
		case 'hu':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/hu" */ '../../../i18n/hu'
			);
		case 'it':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/it" */ '../../../i18n/it'
			);
		case 'ja':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/ja" */ '../../../i18n/ja'
			);
		case 'ko':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/ko" */ '../../../i18n/ko'
			);
		case 'nb':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/nb" */ '../../../i18n/nb'
			);
		case 'nl':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/nl" */ '../../../i18n/nl'
			);
		case 'pl':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/pl" */ '../../../i18n/pl'
			);
		case 'pt-BR':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/pt_BR" */ '../../../i18n/pt_BR'
			);
		case 'ru':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/ru" */ '../../../i18n/ru'
			);
		case 'sl':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/sl" */ '../../../i18n/sl'
			);
		case 'sr-RS':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/sr_RS" */ '../../../i18n/sr_RS'
			);
		case 'sr-YR':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/sr_YR" */ '../../../i18n/sr_YR'
			);
		case 'sv':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/sv" */ '../../../i18n/sv'
			);
		case 'th':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/th" */ '../../../i18n/th'
			);
		case 'tr':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/tr" */ '../../../i18n/tr'
			);
		case 'uk':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/uk" */ '../../../i18n/uk'
			);
		case 'vi':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/vi" */ '../../../i18n/vi'
			);
		case 'zh':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/zh" */ '../../../i18n/zh'
			);
		case 'zh-TW':
			return import(
				/* webpackChunkName: "@atlassian/twc-xflow-modal/locale/zh_TW" */ '../../../i18n/zh_TW'
			);
		default:
			throw new Error(`Unsupported locale: ${locale}`);
	}
};

export const negotiateLocale = (requestedLocale: string): AvailableLocale => {
	try {
		const normalizedLocale = requestedLocale.replace(/_/g, '-');

		const matched = match([normalizedLocale], [...AVAILABLE_LOCALES], 'en', {
			algorithm: 'best fit',
		});

		if (isAvailableLocale(matched)) {
			return matched;
		}

		return 'en';
	} catch {
		return 'en';
	}
};

// Keys that need HTML entity and apostrophe normalization across ALL locales
const KEYS_TO_NORMALIZE_ALL_LOCALES = [
	'twc.xflow.modal.legal.noPaymentDetailsLegalTextStandard.v2',
	'twc.xflow.modal.legal.noPaymentDetailsLegalTextPremium.v2',
	'twc.xflow.modal.legal.paymentDetailsLegalText.v2.ubp',
];

const LOCALE_SPECIFIC_NORMALIZATIONS: Record<string, string[]> = {
	it: ['twc.xflow.modal.rovo.description'],
};

const normalizeMessagesForLocale = (
	messages: Record<string, string>,
	locale: AvailableLocale,
): Record<string, string> => {
	const keysToNormalize = new Set([
		...KEYS_TO_NORMALIZE_ALL_LOCALES,
		...(LOCALE_SPECIFIC_NORMALIZATIONS[locale] || []),
	]);

	const normalized = { ...messages };
	keysToNormalize.forEach((key) => {
		if (typeof normalized[key] === 'string') {
			normalized[key] = normalizeMessageText(normalized[key]);
		}
	});

	return normalized;
};

export const loadMessages = async (
	requestedLocale: string,
): Promise<{ locale: AvailableLocale; messages: Record<string, string> }> => {
	const matchedLocale = negotiateLocale(requestedLocale);

	try {
		const localeModule = await importLocaleMessages(matchedLocale);
		const messages = localeModule.default || {};
		return {
			locale: matchedLocale,
			messages: normalizeMessagesForLocale(messages, matchedLocale),
		};
	} catch {
		// Fallback to English if anything goes wrong
		if (matchedLocale !== 'en') {
			try {
				const enModule = await importLocaleMessages('en');
				const messages = enModule.default || {};
				return {
					locale: 'en',
					messages: normalizeMessagesForLocale(messages, 'en'),
				};
			} catch {
				return {
					locale: 'en',
					messages: {},
				};
			}
		}
		return {
			locale: 'en',
			messages: {},
		};
	}
};

export const useMessages = (
	requestedLocale: string,
): { isLoading: boolean; locale: AvailableLocale; messages: Record<string, string> } => {
	const [isLoading, setIsLoading] = useState(true);
	const [intlData, setIntlData] = useState<{
		locale: AvailableLocale;
		messages: Record<string, string>;
	}>({
		locale: 'en',
		messages: {},
	});

	useEffect(() => {
		let isMounted = true;

		const fetchMessages = async () => {
			setIsLoading(true);
			try {
				const data = await loadMessages(requestedLocale);
				if (isMounted) {
					setIntlData(data);
				}
			} catch {
				if (isMounted) {
					setIntlData({ locale: 'en', messages: {} });
				}
			} finally {
				if (isMounted) {
					setIsLoading(false);
				}
			}
		};

		fetchMessages();

		return () => {
			isMounted = false;
		};
	}, [requestedLocale]);

	return {
		isLoading,
		locale: intlData.locale,
		messages: intlData.messages,
	};
};
